import React, { useEffect, useRef, useState } from "react";
import AuctionCard from "../../components/AuctionCard/AuctionCard";
import HeroImage from "../../assets/images/hero-moving.png";
import logo from "../../assets/svg/moves_offer_logo.svg";
import { Link, useNavigate } from "react-router-dom";
import { benefits } from "./benefits.js";
import TestimonialsSection from "./TestimonialsSection";
import { SEO } from "../../components/SEO/SEO";
import { SEO_CONFIG } from "../../components/SEO/seoConfig";
import { createHomeSchema } from "../../components/SEO/seoSchemas";
import { toast } from "react-toastify";

import { useHomePage } from "./useHomePage";

const HomePage: React.FC = () => {
  const {
    // loading,
    auctionsPreview,
    isSubscriptionStatusActive,
    isAuthenticated,
  } = useHomePage();

  const navigate = useNavigate();
  const benefitsRef = useRef<HTMLElement>(null);
  const auctionsRef = useRef<HTMLElement>(null);
  const [isBenefitsVisible, setIsBenefitsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsBenefitsVisible(true);
          }
        });
      },
      { threshold: 0.2 },
    );

    const currentRef = benefitsRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div className="container py-6 space-y-12">
      <SEO {...SEO_CONFIG.home} structuredData={createHomeSchema()} />
      {/* Hero Section */}
      <div className="bg-gray-100 rounded-lg p-8 flex flex-col md:flex-row items-center gap-8 overflow-hidden">
        <div className="flex-1 animate-slide-in-left">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Find the Best Movers for Your Job
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            Post your move. Get bids. Choose the best deal.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate("/create-order")}
              className="bg-primary text-white px-6 py-2 rounded"
            >
              Post Your Move
            </button>
            <button
              className="text-primary underline py-2"
              onClick={() => {
                if (!isAuthenticated) {
                  toast.info("Please sign in to browse all auctions");
                  auctionsRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                } else {
                  navigate("/auctions");
                }
              }}
            >
              Browse Auctions
            </button>
          </div>
        </div>
        <div
          className="relative w-full md:w-1/2 rounded-lg shadow overflow-hidden animate-slide-in-right min-h-[400px] sm:min-h-[450px]"
          style={{ animationDelay: "0.2s", animationFillMode: "both" }}
        >
          <img
            src={HeroImage}
            alt="Professional movers loading furniture into moving truck - MovesOffer auction platform"
            className="w-full h-full object-cover absolute inset-0"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/30 flex flex-col justify-center px-4 sm:px-6 md:px-10 text-white">
            <h1 className="text-xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 leading-tight">
              Get the best price for your move
            </h1>
            <p className="text-sm sm:text-base md:text-lg mb-4 sm:mb-5 leading-relaxed">
              List your move and receive bids from professional moving
              companies.
            </p>
            {!isAuthenticated && (
              <button
                onClick={() => navigate("/signin")}
                className="bg-primary hover:bg-primary-dark transition-colors px-4 sm:px-6 py-2 sm:py-3 rounded text-white font-semibold w-max text-sm sm:text-base"
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      </div>

      <section
        ref={benefitsRef}
        className="mt-16 bg-gray-50 py-12 rounded-lg shadow-inner overflow-hidden"
      >
        <div className="container text-center">
          <h2 className="text-2xl font-bold mb-8">Why Choose Our Platform?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className={`bg-white p-6 rounded-lg shadow hover:shadow-xl transition-all duration-300 hover:-translate-y-2 animate-float ${
                  isBenefitsVisible
                    ? idx % 2 === 0
                      ? "animate-slide-in-left"
                      : "animate-slide-in-right"
                    : "opacity-0"
                }`}
                style={{
                  animationDelay: isBenefitsVisible ? `${idx * 0.2}s` : "0s",
                  animationFillMode: "both",
                }}
              >
                <div className="mb-4 transform hover:scale-110 transition-transform duration-300">
                  <img
                    src={benefit.icon}
                    alt={benefit.title}
                    className="w-12 h-12 mx-auto"
                  />
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Auction Preview */}
      <section ref={auctionsRef}>
        <h2 className="text-2xl font-semibold mb-4">Current Auctions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {auctionsPreview.map((auction) => (
            <AuctionCard
              key={auction.id}
              onBidSubmit={() => {}}
              mode="preview"
              isSubscriptionActive={isSubscriptionStatusActive}
              auction={auction}
            />
          ))}
        </div>
        {!isAuthenticated && (
          <div className="mt-4">
            <button
              onClick={() => navigate("/signin")}
              className="text-primary hover:underline"
            >
              Authorize to View All Auctions →
            </button>
          </div>
        )}
      </section>

      {/* Branded Divider Section */}
      <Link to={isAuthenticated ? "/auctions" : "/signin"}>
        <section className="py-12">
          <div className="flex flex-col items-center justify-center">
            <div className="relative group animate-float">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/30 to-primary/20 rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl"></div>
                <img
                  src={logo}
                  alt="Moves Offer"
                  className="h-24 w-24 relative z-10 rounded-full shadow-2xl transform group-hover:scale-110 transition-all duration-300"
                />
              </div>
            </div>
            <div className="mt-6 text-center">
              <h3 className="text-3xl font-bold text-gray-800 mb-2">
                Trusted Moving Platform
              </h3>
              <p className="text-gray-600 max-w-md mb-6">
                Connecting customers with moving companies across North America
              </p>
            </div>
          </div>
        </section>
      </Link>

      <TestimonialsSection />
    </div>
  );
};

export default HomePage;
